import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
} from 'react'

interface DarkModeContextType {
    isDarkMode: boolean
    setDarkMode: (value: boolean) => void
    syncFromApi: (value: boolean) => void
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(
    undefined
)

export const DarkModeProvider: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const saved = localStorage.getItem('darkMode')
        return saved ? JSON.parse(saved) : false
    })

    const isDarkModeRef = useRef(isDarkMode)

    useEffect(() => {
        isDarkModeRef.current = isDarkMode
    }, [isDarkMode])

    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.setAttribute('data-theme', 'dark')
        } else {
            document.documentElement.removeAttribute('data-theme')
        }
        localStorage.setItem('darkMode', JSON.stringify(isDarkMode))
    }, [isDarkMode])

    const syncFromApi = useCallback((apiValue: boolean) => {
        if (apiValue !== isDarkModeRef.current) {
            setIsDarkMode(apiValue)
        }
    }, [])

    return (
        <DarkModeContext.Provider
            value={{ isDarkMode, setDarkMode: setIsDarkMode, syncFromApi }}
        >
            {children}
        </DarkModeContext.Provider>
    )
}

export const useDarkMode = () => {
    const context = useContext(DarkModeContext)
    if (!context) {
        throw new Error('useDarkMode must be used within DarkModeProvider')
    }

    return context
}
